import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, Item, KeyboardNavigation, Paging, Scrolling, Sorting, StateStoring, Toolbar, SearchPanel, Popup, Lookup, Position, RequiredRule, DataGridTypes, Selection } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { useApiConfiguration } from '../../api/api.configuration';
import { SysIAnalitos, SysAAnalitosRangosAlternos, SysAAnalitosUnidadesMedida, SysIAnalitosTipoMuestras, SysACodigoBarras } from '../../types/types.analitos';
import { Analitos_ResultsTypes } from '../../utils/systemTypes';
import { useAppSelector } from '../../contexts/hooks';
import { ModalResultTypes } from './Modals/ModalResultTypes';
import { Barcodes, UnitMeasurement, SampleTypes, AlternateRanks, microbiology } from '../../images/icons';
import { ModalModulesAnalytes } from './Modals/ModalModulesAnalytes';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';

const Index = () => {
  const Prm_Conf_Analito = useAppSelector((x) => x.userInfo.Permissions?.Prm_Conf_Analito);
  const { getAnalitos, setAnalito, newAnalito, deleteAnalito, getListasAnalitos } = useApiConfiguration();
  const [data, setData] = useState<SysIAnalitos[]>([]);
  const [rangosAlternos, setRangosAlternos] = useState<SysAAnalitosRangosAlternos[]>([]);
  const [unidadesMedida, setUnidadesMedida] = useState<SysAAnalitosUnidadesMedida[]>([]);
  const [tipoMuestra, setTipoMuestra] = useState<SysIAnalitosTipoMuestras[]>([]);
  const [codBarras, setCodBarras] = useState<SysACodigoBarras[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalModuleVisible, setIsModalModuleVisible] = useState(false);
  const [idAnalito, setIdAnalito] = useState(-1);
  const [tipoResultado, setTipoResultado] = useState(-1);
  const [valRefText, SetValRefText] = useState({});
  const [configPlantillas, setConfigPlantillas] = useState<any>({});
  const [analito, setAnalitos] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [modulo, setModulo] = useState<number>(0);
  const dataGridRef = useRef<DataGrid>(null);

  useEffect(() => {
    getListasAnalitos().then((res) => {
      setRangosAlternos(res.RangoAlternos);
      setUnidadesMedida(res.UnidadesMedida);
      setTipoMuestra(res.TiposMuestras);
      setCodBarras(res.ConBarras);
    });
    getAnalitos().then((res) => {
      setData(res);
    });
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'An_Id',
      loadMode: 'raw', // carga una vez y busca/ordena en memoria → la búsqueda no recarga ni roba el foco
      load() {
        return getAnalitos().catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        return newAnalito({ Analito: values })
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando el analito');
          });
      },
      update: (key, values) => {
        return new Promise((resolve, reject) => {
          dataGridRef.current?.instance
            .byKey(key)
            .then((originalRecord) => {
              // Mezclar el registro original con los valores modificados
              const fullRecord = { ...originalRecord, ...values };

              setAnalito({ Analito: fullRecord, IdAnalito: key })
                .then((res) => {
                  if (res === 107) {
                    dataGridRef.current?.instance.refresh();
                  }
                  resolve(res);
                })
                .catch((error) => {
                  reject(new Error('Error actualizando el analito'));
                });
            })
            .catch(reject);
        });
      },
      remove: (key) => {
        return deleteAnalito({ IdAnalito: key }).then((res) => {
          if (res === 107) {
            dataGridRef.current?.instance.refresh();
          }
        });
      },
    });
  }, []);

  const customDataSrc = useMemo(
    () =>
      new DataSource({
        store: customData,
        reshapeOnPush: true,
      }),
    [customData]
  );

  const handleConfigureClick = useCallback(
    (e) => {
      dataGridRef.current?.instance.refresh();
      const { data }: { data: SysIAnalitos } = e.row;
      setIsModalVisible(true);
      setIdAnalito(data.An_Id);
      setTipoResultado(data.An_Tr_Id);
      setAnalitos(data.An_Nombre);
      SetValRefText({ An_ValRefText: data.An_ValRefText ?? '', An_ValCritico: data.An_ValCritico ?? '', An_ValRefTextPermitidos: data.An_ValRefTextPermitidos ?? '' });
      setConfigPlantillas(data.An_Plantilla ?? {});
    },
    [idAnalito]
  );

  const handleDuplicateClick = useCallback(
    (e) => {
      const { data }: { data: SysIAnalitos } = e.row;
      const duplicated = {
        ...data,
        An_Id: undefined,
        An_Nombre: `${data.An_Nombre} (Copia)`,
        An_Abreviatura: `${data.An_Abreviatura}1`,
      };
      delete duplicated.An_Id;
      delete (duplicated as any).TipoMuestra;
      newAnalito({ Analito: duplicated })
        .then((res) => {
          if (res === 105) {
            dataGridRef.current?.instance.refresh();
          }
        })
        .catch(() => {
          throw new Error('Error duplicando el analito');
        });
    },
    []
  );

  const handleSamplesTypes = useCallback(() => {
    setIsModalModuleVisible(true);
    setModulo(1);
  }, []);

  const handleAlternateRanks = useCallback(() => {
    setIsModalModuleVisible(true);
    setModulo(2);
  }, []);

  const handleBarcodes = useCallback(() => {
    setIsModalModuleVisible(true);
    setModulo(3);
  }, []);

  const handleUnitMeasurement = useCallback(() => {
    setIsModalModuleVisible(true);
    setModulo(4);
  }, []);

  const handleMicrobiology = useCallback(() => {
    setIsModalModuleVisible(true);
    setModulo(5);
  }, []);

  const handleSelection = useCallback(
    ({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
      const dataGrid = selectedRowsData[0];
      setSelectedRow(dataGrid);
    },
    [idAnalito]
  );

  const handleUpdateData = useCallback(() => {
    dataGridRef?.current?.instance.refresh();
  }, []);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid className='grid' dataSource={customDataSrc} ref={dataGridRef} width={'100%'} hoverStateEnabled={true} height='100vh' repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true} onSelectionChanged={handleSelection}>
          <SearchPanel visible={true} width={320} placeholder='Buscar analitos...' />
          <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
          <Sorting mode='multiple' />
          <Selection mode='single' />
          <Editing mode='popup' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
            <Popup title='INFORMACIÓN ANALITO' showTitle={true}>
              <Position my='center' at='center' of={window} />
            </Popup>
          </Editing>
          <Paging enabled={true} />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Analitos</div>
            </Item>
            <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='TiposMuestras' icon={SampleTypes} text='Tipos muestras' stylingMode='text' visible={true} disabled={false} onClick={handleSamplesTypes} />
              <Tooltip target={'#TiposMuestras'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Tipos de muestra
              </Tooltip>
            </Item>
            <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RangoAlternos' icon={AlternateRanks} text='Rangos alternos' stylingMode='text' visible={true} disabled={false} onClick={handleAlternateRanks} />
              <Tooltip target={'#RangoAlternos'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Rangos alternos
              </Tooltip>
            </Item>
            <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='UnidadMedida' icon={UnitMeasurement} text='Tipos muestras' stylingMode='text' visible={true} disabled={false} onClick={handleUnitMeasurement} />
              <Tooltip target={'#UnidadMedida'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Unidad medida
              </Tooltip>
            </Item>
            <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='Etiquetas' icon={Barcodes} text='Etiquetas' stylingMode='text' visible={true} disabled={false} onClick={handleBarcodes} />
              <Tooltip target={'#Etiquetas'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Etiquetas código de barras
              </Tooltip>
            </Item>
            <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='Microbiologia' icon={microbiology} text='Microbiologia' stylingMode='text' visible={true} disabled={false} onClick={handleMicrobiology} />
              <Tooltip target={'#Microbiologia'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Microbiologia
              </Tooltip>
            </Item>
            <Item name='searchPanel' />
            <Item name='addRowButton' />
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrecarResult' icon='refresh' text='Refresh' stylingMode='text' onClick={handleUpdateData} />
              <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Item>
          </Toolbar>
          <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
          <Paging enabled={false} />
          <StateStoring enabled={true} type='custom' />
          <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='An_Id' width={90} alignment='center' name='An_Id' />
          <Column allowGrouping={false} caption='ANALITO' visible={true} allowEditing={true} dataField='An_Nombre' alignment='left' minWidth={200}>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='ABREVIATURA' allowEditing={true} allowSorting={false} dataField='An_Abreviatura' width={120} alignment='center'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='MUESTRA' visible={false} allowEditing={true} dataField='An_Tm_Id' width={75} alignment='center'>
            <Lookup dataSource={tipoMuestra} valueExpr='Tm_Id' displayExpr='Tm_Nombre' allowClearing={true} />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='RANGO ALTERNO' visible={false} allowEditing={true} dataField='An_Ra_Id' width={75} alignment='center'>
            <Lookup dataSource={rangosAlternos} valueExpr='Ra_Id' displayExpr='Ra_Nombre' allowClearing={true} />
          </Column>
          <Column allowGrouping={false} caption='CÓDIGO DE BARRAS' visible={false} allowEditing={true} dataField='An_Cb_Id' width={75} alignment='center'>
            <Lookup dataSource={codBarras} valueExpr='Cb_Id' displayExpr='Cb_Nombre' allowClearing={true} />
          </Column>
          <Column allowGrouping={false} caption='T. RESULTADO' visible={true} allowEditing={true} dataField='An_Tr_Id' width={120} alignment='center'>
            <Lookup dataSource={Analitos_ResultsTypes} valueExpr={'Value'} displayExpr={'State'} />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='UNIDAD' visible={true} allowEditing={true} dataField='An_Um_Id' width={75} alignment='center'>
            <Lookup dataSource={unidadesMedida} valueExpr='Um_Id' displayExpr='Um_Nombre' allowClearing={true} />
          </Column>
          <Column allowGrouping={false} allowSorting={false} caption='DECIMALES' visible={false} showInColumnChooser={false} allowEditing={true} dataField='An_Decimales' width={100} alignment='center' />
          <Column allowGrouping={false} allowSorting={false} caption='HOMOLOGACION' visible={true} showInColumnChooser={false} allowEditing={true} dataField='An_Homologacion' width={130} alignment='center' />
          <Column allowGrouping={false} allowSorting={false} caption='CONSERVAR CEROS DESP. DECIMAL?' visible={false} showInColumnChooser={false} allowEditing={true} dataField='An_ConservarCerosDecimales' width={100} alignment='center' />
          <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='An_Estado' alignment='center' width={90}></Column>
          <Column type='buttons' caption={'ACCIÓN'} width={130}>
            <Butt name='edit' />
            <Butt name='delete' />
            <Butt hint='Duplicar' icon='copy' visible={true} disabled={false} onClick={handleDuplicateClick} />
            <Butt hint='Configurar' icon='preferences' visible={true} disabled={false} onClick={handleConfigureClick} />
          </Column>
        </DataGrid>
        <ModalResultTypes key={idAnalito} visible={isModalVisible} setVisible={() => setIsModalVisible(false)} TipoResultado={tipoResultado} IdAnalito={idAnalito} Analito={analito} ValRefText={valRefText} configPlantillas={configPlantillas} Analytes={data} />
        <ModalModulesAnalytes visible={isModalModuleVisible} setVisible={() => setIsModalModuleVisible(false)} Modulo={modulo} />
      </div>
    </div>
  );
};

export default Index;
